import { useCallback, useEffect, useRef, useState } from 'react';
import { ServerSetting, getServerSettings } from '../../../services/adminServerSettings';
import {
  OverviewPart,
  UnitsOverview,
  getJobInterruptions,
  getMissingImagePackages,
  getUnitCosts,
  getUnitsOverview,
} from '../../../services/adminUnits';
import AdminPage from '../AdminPage';
import { failureReason } from '../adminErrors';
import InterruptionsList from './InterruptionsList';
import KnownImages from './KnownImages';
import LiveUnits from './LiveUnits';
import MissingPackagesList from './MissingPackagesList';
import UnitManagerCard from './UnitManagerCard';
import UnitsCosts from './UnitsCosts';
import UnitsPolicyForm from './UnitsPolicyForm';
import UnitsSection from './UnitsSection';
import UnitsSizing from './UnitsSizing';
import UnitsSwitchState from './UnitsSwitchState';

const UnitsRefreshMs = 15_000;

interface Loaded<T> {
  data: T | null;
  /** Why the last request failed; null while it did not. */
  error: string | null;
}

/** A reload while the previous one is unanswered returns that one and sends no second request. */
function useLoaded<T>(fetch: () => Promise<T>) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<Promise<void> | null>(null);
  const reload = useCallback(() => {
    pending.current ??= fetch()
      .then((value) => {
        setData(value);
        setError(null);
      })
      .catch((err) => setError(failureReason(err)))
      .finally(() => {
        pending.current = null;
      });
    return pending.current;
  }, [fetch]);
  return { data, setData, error, reload };
}

const loadError = (part: string, reason: string | null) => (reason ? `Could not load ${part}: ${reason}` : null);

interface Part<T> {
  /** The server answered the part without an error, now or at an earlier reading. */
  loaded: boolean;
  value: T | null;
  error: string | null;
}

/** A part of the overview: a failed request fails every part, and the server's error for a part fails that one. */
function overviewPart<T>(overview: Loaded<UnitsOverview>, name: string, pick: (read: UnitsOverview) => OverviewPart<T>): Part<T> {
  const part = overview.data ? pick(overview.data) : null;
  return {
    loaded: part != null && part.error == null,
    value: part?.value ?? null,
    error: loadError(name, overview.error ?? part?.error ?? null),
  };
}

const Loading = () => <div className="muted">Loading…</div>;

export default function UnitsPage() {
  const overview = useLoaded(getUnitsOverview);
  const settings = useLoaded(getServerSettings);
  const interruptions = useLoaded(getJobInterruptions);
  const packages = useLoaded(getMissingImagePackages);
  const costs = useLoaded(getUnitCosts);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const onDraft = useCallback(
    (key: string, draft: string) => setDrafts((current) => (current[key] === draft ? current : { ...current, [key]: draft })),
    []
  );
  const reloadAll = useCallback(() => {
    void overview.reload();
    void settings.reload();
    void interruptions.reload();
    void packages.reload();
    void costs.reload();
  }, [overview.reload, settings.reload, interruptions.reload, packages.reload, costs.reload]);

  useEffect(() => {
    reloadAll();
    const timer = setInterval(() => {
      void overview.reload();
      void settings.reload();
    }, UnitsRefreshMs);
    return () => clearInterval(timer);
  }, [reloadAll, overview.reload, settings.reload]);

  const onSaved = (saved: ServerSetting) =>
    settings.setData((list) => list?.map((setting) => (setting.key === saved.key ? saved : setting)) ?? null);
  const onImageUsed = (saved: ServerSetting) => {
    onSaved(saved);
    void overview.reload();
  };

  const asOf = overview.data?.asOf ?? '';
  const manager = overviewPart(overview, 'the manager status', (read) => read.manager);
  const live = overviewPart(overview, 'the live units', (read) => read.live);
  const images = overviewPart(overview, 'the known images', (read) => read.images);
  const sizing = overviewPart(overview, 'the sizing', (read) => read.sizing);

  return (
    <AdminPage
      active="admin-units"
      eyebrow="Pipeline workers"
      title="Units"
      intro="One pipeline worker per unit, on Azure Container Instances."
      wide
      rightSlot={
        <button type="button" className="btn sm" onClick={reloadAll}>
          Refresh
        </button>
      }
    >
      {settings.data ? <UnitsSwitchState settings={settings.data} /> : !settings.error && <Loading />}

      <UnitsSection title="Unit manager" description="Status every 15 s" error={manager.error}>
        {manager.loaded ? (
          <UnitManagerCard manager={manager.value} asOf={asOf} settings={settings.data} />
        ) : (
          !manager.error && <Loading />
        )}
      </UnitsSection>

      <UnitsSection title="Live units" description="Refreshes every 15 s" error={live.error}>
        {live.value ? <LiveUnits live={live.value} asOf={asOf} /> : !live.error && <Loading />}
      </UnitsSection>

      <UnitsSection title="Sizing" description="Ended units, last 7 days" error={sizing.error}>
        {sizing.value ? <UnitsSizing sizing={sizing.value} settings={settings.data} /> : !sizing.error && <Loading />}
      </UnitsSection>

      <UnitsSection
        title="Worker images"
        description="Last 10 recorded. New units start from the current one."
        error={images.error}
      >
        {images.value ? (
          <KnownImages images={images.value} settings={settings.data} onUsed={onImageUsed} />
        ) : (
          !images.error && <Loading />
        )}
      </UnitsSection>

      <UnitsSection title="Policy" error={loadError('the policy', settings.error)}>
        {settings.data ? (
          <UnitsPolicyForm settings={settings.data} manager={manager.value} onSaved={onSaved} onDraft={onDraft} />
        ) : (
          !settings.error && <Loading />
        )}
      </UnitsSection>

      <UnitsSection title="Costs" error={loadError('the costs', costs.error)}>
        {costs.data ? (
          <UnitsCosts costs={costs.data} settings={settings.data} drafts={drafts} />
        ) : (
          !costs.error && <Loading />
        )}
      </UnitsSection>

      <UnitsSection
        title="Interruptions"
        description="Last 100 attempts the platform stopped"
        error={loadError('the interruptions', interruptions.error)}
      >
        {interruptions.data ? <InterruptionsList interruptions={interruptions.data} /> : !interruptions.error && <Loading />}
      </UnitsSection>

      <UnitsSection
        title="Packages missing from the image"
        description="Last 100 that jobs restored"
        error={loadError('the missing packages', packages.error)}
      >
        {packages.data ? <MissingPackagesList packages={packages.data} /> : !packages.error && <Loading />}
      </UnitsSection>
    </AdminPage>
  );
}
